import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isCallSessionLive,
  sessionElapsedSeconds,
  useEmpathyQueueStore,
} from '../../stores/empathy-queue.store';
import type { GiverState, SeekerState } from '../../types';
import { countOf } from '../../utils/plural';
import { safeGetJSON, safeSetJSON } from '../../utils/storage';
import { useFullScreenStageOpen } from '../stage/fullScreenStage';
import { type CallCapView, callCapView, capRemainingSeconds } from './callCap';
import { CALL_ROUTE } from './callRoutes';
import {
  PILL_COMPACT_PX,
  PILL_PANEL_MAX_HEIGHT_PX,
  PILL_PANEL_WIDTH_PX,
  queuePillKind,
  queuePillName,
} from './queuePill';
import { useWaitingCardShown } from './waitingCardVisibility';

interface Position {
  x: number;
  y: number;
}

interface QueueOverlayProps {
  mode: 'giver' | 'seeker';
  giverState?: GiverState;
  seekerState?: SeekerState;
  queuePosition?: number | null;
  estimatedWaitMinutes?: number | null;
  reliabilityScore?: number | null;
  availableGiversCount?: number;
  /** Leave the QUEUE — never a live call. The call's end is the store's. */
  onLeave: () => void;
  isLeaving?: boolean;
}

/**
 * The overlay's own box, said once. Three separate copies of these numbers (the
 * initial constrain, the resize constrain and the drag) is how a clamp ends up
 * fixed in one of the three; the panel also carries them as an inline style, so
 * the box this function clamps is the box the browser draws.
 */
const overlaySize = (expanded: boolean): { widthPx: number; heightPx: number } => ({
  widthPx: expanded ? PILL_PANEL_WIDTH_PX : PILL_COMPACT_PX,
  heightPx: expanded ? PILL_PANEL_MAX_HEIGHT_PX : PILL_COMPACT_PX,
});

/**
 * The part of the screen the user can see, in layout-viewport px (the space
 * `position: fixed` uses): the VISUAL viewport's offset plus its size. A
 * raised keyboard (and pinch-zoom) shrinks only the visual viewport, so the
 * window's size would let the pill sit in the band the keyboard hides
 * (b-175; the #869 class). Every term falls back to the window on its own.
 */
const visibleBox = (): { left: number; top: number; right: number; bottom: number } => {
  const vv = window.visualViewport;
  const left = vv?.offsetLeft ?? 0;
  const top = vv?.offsetTop ?? 0;
  return {
    left,
    top,
    right: left + (vv?.width ?? window.innerWidth),
    bottom: top + (vv?.height ?? window.innerHeight),
  };
};

/** Where this overlay goes when nothing has ever told it otherwise. */
const defaultPosition = (): Position => {
  const { widthPx, heightPx } = overlaySize(true);
  const box = visibleBox();
  return {
    x: Math.max(box.left, box.left + (box.right - box.left - widthPx) / 2),
    y: Math.max(box.top, box.top + (box.bottom - box.top - heightPx) / 2),
  };
};

/**
 * Keep the overlay on the screen.
 *
 * ONE function and not a second copy inside the drag handler: the drag had its
 * own transcription of the same arithmetic, so a bound added here would have
 * held on mount and on resize and let go the moment anybody touched the pill
 * (class D).
 */
const constrainPosition = (pos: Position, expanded: boolean): Position => {
  const { widthPx, heightPx } = overlaySize(expanded);
  const box = visibleBox();
  return {
    x: Math.max(box.left, Math.min(box.right - widthPx, pos.x)),
    y: Math.max(box.top, Math.min(box.bottom - heightPx, pos.y)),
  };
};

/**
 * THE CALL'S CLOCK, READ NOT RESTARTED — AND DECIDED IN ONE PLACE.
 *
 * The anchor lives in the store and is keyed by the session, so the elapsed
 * second here and the stage's are the same second — see `sessionElapsedSeconds`.
 * The DEADLINE comes from the same store field the stage reads, and what to
 * say with the two of them is `callCapView`'s decision, not this component's:
 * near the cap the stage counts down and says that it is wrapping up, and
 * a pill that went on counting up was the same call wearing two clocks.
 *
 * Ticking only while there is something to tick for, because this component is
 * mounted on every dashboard page.
 */
function useCallCap(live: boolean): CallCapView | null {
  const currentSessionId = useEmpathyQueueStore((s) => s.currentSessionId);
  const sessionAnchor = useEmpathyQueueStore((s) => s.sessionAnchor);
  const sessionEndsAt = useEmpathyQueueStore((s) => s.sessionEndsAt);
  // The span, beside the deadline: what the pill says near the end is scaled to
  // how long the call was — a three-minute call does not wrap up for five.
  const sessionCapSeconds = useEmpathyQueueStore((s) => s.sessionCapSeconds);
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!live || !sessionAnchor) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [live, sessionAnchor]);

  if (!live) return null;

  const elapsed = sessionElapsedSeconds({ currentSessionId, sessionAnchor }, now);
  if (elapsed === null) return null;

  return callCapView(elapsed, capRemainingSeconds(sessionEndsAt, now), sessionCapSeconds);
}

/**
 * The compact circle takes the TILE's colour (A7, David 2026-10-18): the
 * same two violets as the Give (plum) and Receive (violet-700) tiles on the
 * 1-on-1 page (R13, PR #1001), so the bubble reads as "the thing you started
 * there". The panels are unchanged.
 */
const ACCENTS = {
  green: {
    panel:
      'bg-gradient-to-br from-(--ee-tw-green-50-bg) to-(--ee-tw-emerald-50-bg) border-(--ee-tw-green-300-line)',
    circle: 'tile-lit bubble-lit-give',
  },
  blue: {
    panel:
      'bg-gradient-to-br from-(--ee-tw-blue-50-bg) to-(--ee-tw-sky-50-bg) border-(--ee-tw-blue-300-line)',
    circle: 'tile-lit bubble-lit-receive',
  },
  amber: {
    panel: 'bg-(--ee-tw-amber-50-bg) border-(--ee-tw-amber-300-line)',
    circle: 'bg-gradient-to-br from-(--ee-tw-amber-300-bg) to-(--ee-tw-amber-500-bg)',
  },
} as const;

type Accent = keyof typeof ACCENTS;

/** One row of the wait's detail — the label on the left, the fact on the right. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-(--ee-tw-gray-600)">{label}</span>
      <span className="font-bold text-(--ee-tw-gray-800)">{children}</span>
    </div>
  );
}

/**
 * QueueOverlay — the floating pill for the empathy queue and for a call you
 * have navigated away from.
 *
 * NOT RENDERED WHILE A CALL STAGE IS UP, which is the whole shape of this
 * component. David, 2026-09-14, after the pill covered the call's top bar on a
 * phone: End lives in the stage's control row and the state and the clock live
 * in its top bar, so a second floating copy of them can only be in the way.
 * (The first fix parked it in the band the stage leaves free; this supersedes
 * it — the band arithmetic went with it.)
 *
 * NOT AFTER THE CALL EITHER. `giverState` still reads `ACTIVE` while the
 * feedback screen is up — `leaveGiverQueue` is deferred until the post-session
 * flow closes — so the pill used to sit over that screen offering to return to
 * a call that had ended. `isCallSessionLive` is the one question that separates
 * "the queue says you are in a call" from "there is a call to go back to", and
 * the Back handler asks the same one.
 *
 * EVERY STATE NAMES ITS ACTION. A spinner with no verb is a state you cannot
 * leave, so the queue offers "Leave queue", a call you walked away from offers
 * "Return to call" and "End call", and each of those runs the SAME function the
 * rest of the app runs for it rather than a second description of it.
 */
export default function QueueOverlay({
  mode,
  giverState,
  seekerState,
  queuePosition,
  estimatedWaitMinutes,
  reliabilityScore,
  availableGiversCount,
  onLeave,
  isLeaving = false,
}: QueueOverlayProps) {
  const navigate = useNavigate();

  /**
   * THE SAME END THE STAGE'S CONTROL ROW RUNS — `requestEndSession` on the
   * empathy queue store, which the mounted flow turns into the
   * teardown-plus-feedback path (class C: two buttons, one description of
   * leaving a call). `PeerAudioRoom` hands this identical action to the stage's
   * End button.
   */
  const requestEndSession = useEmpathyQueueStore((s) => s.requestEndSession);

  // Animate queue position changes smoothly
  const [displayPosition, setDisplayPosition] = useState<number | null | undefined>(queuePosition);
  const [isPositionAnimating, setIsPositionAnimating] = useState<boolean>(false);
  const prevQueuePosition = useRef<number | null | undefined>(queuePosition);

  useEffect(() => {
    if (queuePosition !== prevQueuePosition.current && queuePosition !== null) {
      setIsPositionAnimating(true);
      const timer = setTimeout(() => {
        setDisplayPosition(queuePosition);
        prevQueuePosition.current = queuePosition;
        setTimeout(() => setIsPositionAnimating(false), 300);
      }, 150);
      return () => clearTimeout(timer);
    } else if (queuePosition !== null && displayPosition === null) {
      // Initial set
      setDisplayPosition(queuePosition);
      prevQueuePosition.current = queuePosition;
    }
  }, [queuePosition, displayPosition]);

  /**
   * COMPACT BY DEFAULT. The pill is a status light that can be asked for its
   * verbs, not a card that has to be dismissed before you can read the page
   * underneath it.
   */
  const [isExpanded, setIsExpanded] = useState(() =>
    safeGetJSON<boolean>('queueOverlayExpanded', false),
  );

  const stageOpen = useFullScreenStageOpen();
  // A7: on the 1-on-1 page the waiting card (A4/A5) says what the bubble would.
  const waitingCardShown = useWaitingCardShown((s) => s.count > 0);

  /**
   * IS THERE A CALL TO GO BACK TO — the ONE predicate, asked with the states
   * this component actually renders from.
   *
   * The giver/seeker states come from PROPS (the dashboard layout reads them
   * off the store and hands them down), so they are spread over the store's
   * own copy rather than read out of it: the set measured here has to be the
   * set rendered below (class C). The selector returns a boolean, so the pill
   * re-renders when the answer changes and not when the store does.
   */
  const callLive = useEmpathyQueueStore((s) =>
    isCallSessionLive({ ...s, giverState, seekerState }),
  );

  const kind = queuePillKind({ mode, giverState, seekerState, callLive });
  const cap = useCallCap(kind === 'in-call');
  const accessibleName = queuePillName({
    mode,
    giverState,
    seekerState,
    callLive,
    queuePosition: displayPosition,
    cap,
  });

  const [position, setPosition] = useState<Position>(() => {
    const expanded = safeGetJSON<boolean>('queueOverlayExpanded', false);
    const savedPos = safeGetJSON<Position | null>('queueOverlayPosition', null);
    return constrainPosition(savedPos ?? defaultPosition(), expanded);
  });

  const [isDragging, setIsDragging] = useState<boolean>(false);
  // Use ref for dragDistance to ensure synchronous access in click handlers
  // State updates are async and can cause race conditions with click events
  const dragDistanceRef = useRef<number>(0);
  const dragStartPos = useRef<Position>({ x: 0, y: 0 });
  const elementStartPos = useRef<Position>({ x: 0, y: 0 });

  // Save position and expanded state to storage (safe for Safari private mode)
  useEffect(() => {
    safeSetJSON('queueOverlayPosition', position);
  }, [position]);

  useEffect(() => {
    safeSetJSON('queueOverlayExpanded', isExpanded);
  }, [isExpanded]);

  // Auto-minimize only when first entering active session (not continuously).
  // The SAME `callLive` the pill renders from — this is the third place in
  // this component that asked "is there a live call?", and a third
  // transcription of it is a third thing to forget about a feedback screen
  // (class D).
  const wasInActiveSession = useRef(false);
  useEffect(() => {
    if (callLive && !wasInActiveSession.current) {
      setIsExpanded(false);
    }
    wasInActiveSession.current = callLive;
  }, [callLive]);

  // Keep the overlay on screen — including the expanded panel, whose whole
  // box is clamped rather than only its corner.
  useEffect(() => {
    const handleResize = (): void => {
      setPosition((prev) => constrainPosition(prev, isExpanded));
    };
    window.addEventListener('resize', handleResize);
    // The keyboard rising shrinks only the visual viewport and fires no
    // window resize on iOS; pinch and pan move it and fire `scroll`.
    const vv = window.visualViewport;
    vv?.addEventListener('resize', handleResize);
    vv?.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      vv?.removeEventListener('resize', handleResize);
      vv?.removeEventListener('scroll', handleResize);
    };
  }, [isExpanded]);

  // Expanding is what makes the box bigger, so it is what can push it off the
  // bottom of a 640px phone. Re-clamped here rather than at the click, so the
  // restored-from-storage case goes through the same one bound.
  useEffect(() => {
    setPosition((prev) => constrainPosition(prev, isExpanded));
  }, [isExpanded]);

  /**
   * FOCUS COMES BACK TO THE PILL when the panel closes. Without it the focus
   * ring lands on `<body>` and a keyboard or switch user has to tab the length
   * of the page to reach the thing they just closed.
   */
  const compactRef = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);
  const collapse = useCallback((): void => {
    returnFocus.current = true;
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    if (isExpanded || !returnFocus.current) return;
    returnFocus.current = false;
    compactRef.current?.focus();
  }, [isExpanded]);

  // Handle drag start (mouse and touch)
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const clientX =
        e.type === 'touchstart'
          ? (e as React.TouchEvent).touches[0].clientX
          : (e as React.MouseEvent).clientX;
      const clientY =
        e.type === 'touchstart'
          ? (e as React.TouchEvent).touches[0].clientY
          : (e as React.MouseEvent).clientY;

      setIsDragging(true);
      dragDistanceRef.current = 0; // Reset synchronously for reliable click detection
      dragStartPos.current = { x: clientX, y: clientY };
      elementStartPos.current = { x: position.x, y: position.y };
    },
    [position],
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const clientX =
        e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      const clientY =
        e.type === 'touchmove' ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

      const deltaX = clientX - dragStartPos.current.x;
      const deltaY = clientY - dragStartPos.current.y;

      // Calculate total drag distance (use ref for synchronous access)
      dragDistanceRef.current = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      setPosition(
        constrainPosition(
          {
            x: elementStartPos.current.x + deltaX,
            y: elementStartPos.current.y + deltaY,
          },
          isExpanded,
        ),
      );
    },
    [isDragging, isExpanded],
  );

  // Handle drag end
  const handleDragEnd = useCallback((): void => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners for drag when dragging
  // Using passive: false for touchmove to allow preventDefault if needed for drag
  // Mouse events don't need passive option
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      // Touch events: passive: false allows us to prevent scroll while dragging
      // This is intentional for drag UX - we don't want page to scroll during drag
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd, { passive: true });

      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Prevent click from expanding when dragging (only expand if drag distance < 5px)
  const handleClick = (): void => {
    // Using ref ensures we read the current value synchronously
    if (dragDistanceRef.current < 5) {
      // A7·3: while you wait, the bubble's verb is "back to the waiting
      // screen", where Leave / Cancel live. A call you walked away from and a
      // cooldown keep their panel.
      if (kind === 'queued') {
        void navigate(CALL_ROUTE);
        return;
      }
      setIsExpanded((wasExpanded) => !wasExpanded);
    }
  };

  const returnToCall = (): void => {
    setIsExpanded(false);
    void navigate(CALL_ROUTE);
  };

  const endCall = (): void => {
    setIsExpanded(false);
    requestEndSession();
    // Back to the page that WATCHES for that flag: the flow component runs the
    // teardown and the feedback prompt, and it only exists on the empathy page.
    void navigate(CALL_ROUTE);
  };

  /**
   * NOT WHILE THE STAGE IS UP, and nothing of it left behind either — a hidden
   * but rendered pill still takes taps on a phone. Placed after every hook, so
   * the hook order does not change with the stage.
   */
  if (stageOpen || kind === 'hidden') return null;
  if (kind === 'queued' && waitingCardShown) return null;

  const accent: Accent = kind === 'cooldown' ? 'amber' : mode === 'giver' ? 'green' : 'blue';
  const emoji =
    kind === 'cooldown' ? '⏸️' : kind === 'in-call' ? '📞' : mode === 'giver' ? '💝' : '🤲';

  if (!isExpanded || kind === 'queued') {
    return (
      // Why: drag-handle wrapper for repositioning the minimized overlay.
      // Pointer-only drag is the established UX; the inner button below
      // handles the keyboard-accessible click/expand action.
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className="fixed z-(--ee-z-float) cursor-grab active:cursor-grabbing"
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <button
          ref={compactRef}
          type="button"
          data-testid="queue-pill"
          data-pill-kind={kind}
          onClick={handleClick}
          className={`w-16 h-16 rounded-full shadow-lg flex flex-col items-center justify-center gap-0.5 text-(--ee-on-gradient) transition-all hover:scale-110 ${
            ACCENTS[accent].circle
          } ${kind === 'queued' ? 'animate-spin-slow' : ''}`}
          aria-label={accessibleName}
        >
          <span aria-hidden className="text-2xl leading-none">
            {emoji}
          </span>
          {kind === 'in-call' && cap ? (
            /* `compactText`, not `clockText`: this is a 64px circle and the
               sentence version spilled out of both sides of it on a phone
               (2026-09-14). `font-mono tabular-nums` only while it IS a clock,
               so the digits stop shuffling every second without putting "14m"
               in a monospace it does not need. */
            <span
              data-testid="queue-pill-clock"
              aria-hidden
              className={`text-[10px] leading-none ${
                cap.clockIsTime ? 'font-mono tabular-nums' : 'font-semibold'
              }`}
            >
              {cap.compactText}
            </span>
          ) : null}
        </button>
      </div>
    );
  }

  const title =
    kind === 'cooldown'
      ? 'Taking a break'
      : kind === 'in-call'
        ? 'In a call'
        : mode === 'giver'
          ? 'Giving empathy'
          : 'Seeking empathy';

  let body: ReactNode = null;
  let verbs: ReactNode = null;

  if (kind === 'in-call') {
    body = (
      <>
        <p className="text-sm text-(--ee-tw-gray-700)">
          {mode === 'giver' ? "You're giving empathy" : "You're receiving empathy"} — the call is
          still running.
        </p>
        <p
          data-testid="queue-pill-elapsed"
          className={`mt-2 text-2xl ${
            cap?.clockIsTime === false ? 'font-semibold' : 'font-mono tabular-nums'
          } ${cap && cap.phase !== 'counting-up' ? 'text-(--ee-tw-amber-700)' : 'text-(--ee-tw-gray-800)'}`}
        >
          {cap ? cap.clockText : '—'}
        </p>
        {/* The same sentence, in the same words, the stage puts under the faces
            — `callCapView` owns it, so there is no second copy to fall out of
            step with the first. */}
        {cap?.noticeText ? (
          <p data-testid="queue-pill-notice" className="mt-1 text-sm text-(--ee-tw-amber-700)">
            {cap.noticeText}
          </p>
        ) : null}
      </>
    );
    verbs = (
      <>
        <button onClick={returnToCall} className="btn btn-primary btn-sm w-full">
          Return to call
        </button>
        <button onClick={endCall} className="btn btn-error btn-sm w-full">
          End call
        </button>
      </>
    );
  } else if (kind === 'cooldown') {
    body = (
      <>
        <p className="text-sm text-(--ee-tw-gray-700)">You can rejoin the queue shortly.</p>
        <p className="mt-1 text-xs text-(--ee-tw-gray-500) italic">
          (After 3 &quot;Not Ready Yet&quot; responses)
        </p>
      </>
    );
    verbs = (
      <button onClick={onLeave} disabled={isLeaving} className="btn btn-ghost btn-sm w-full">
        {isLeaving ? <span className="loading loading-spinner loading-xs"></span> : 'Close'}
      </button>
    );
  } else {
    body = (
      <>
        <div className="space-y-2">
          {mode === 'giver' ? (
            <>
              {giverState === 'PINGED' ? (
                <DetailRow label="Status">
                  <span className="text-(--ee-tw-green-600)">Ready pool</span>
                </DetailRow>
              ) : null}
              {displayPosition ? (
                <DetailRow label="Queue position">
                  <span
                    className={`transition-all duration-300 ease-out ${
                      isPositionAnimating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
                    }`}
                  >
                    #{displayPosition}
                  </span>
                </DetailRow>
              ) : null}
              {estimatedWaitMinutes ? (
                <DetailRow label="Estimated wait">
                  ~{countOf(estimatedWaitMinutes, 'minute')}
                </DetailRow>
              ) : null}
              {reliabilityScore !== null && reliabilityScore !== undefined ? (
                <DetailRow label="Reliability">
                  <span className="text-lg">{'⭐'.repeat(Math.round(reliabilityScore / 20))}</span>
                </DetailRow>
              ) : null}
            </>
          ) : (
            <>
              <DetailRow label="Status">
                <span className="text-(--ee-tw-blue-600)">
                  {seekerState === 'FALLBACK_ACTIVITY'
                    ? 'No givers free yet'
                    : seekerState === 'WAITING'
                      ? 'Asking your group'
                      : 'Finding a giver'}
                </span>
              </DetailRow>
              {availableGiversCount !== undefined ? (
                <DetailRow label="Available givers">{availableGiversCount}</DetailRow>
              ) : null}
            </>
          )}
        </div>

        <p className="mt-3 rounded bg-(--ee-surface)/60 p-3 text-xs text-(--ee-tw-gray-700)">
          {mode === 'giver'
            ? giverState === 'PINGED'
              ? "You're in the ready pool. Match coming soon!"
              : "You'll be pinged when someone needs support"
            : seekerState === 'FALLBACK_ACTIVITY'
              ? "We're still looking! Try an activity while you wait."
              : seekerState === 'WAITING'
                ? // P7 (D28): leaving the queue does not take a group request down.
                  "Your request stays up on your group's Empathy tab for 4 hours, even if you leave the queue."
                : 'Checking available givers — usually 10-20 seconds.'}
        </p>
      </>
    );
    verbs = (
      <button onClick={onLeave} disabled={isLeaving} className="btn btn-error btn-sm w-full">
        {isLeaving ? <span className="loading loading-spinner loading-xs"></span> : 'Leave queue'}
      </button>
    );
  }

  return (
    <div
      data-testid="queue-pill-panel"
      data-pill-kind={kind}
      className={`fixed z-(--ee-z-float) flex flex-col overflow-hidden rounded-lg border-2 shadow-xl ${ACCENTS[accent].panel}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        // The box this panel is CLAMPED to is the box it is DRAWN as — see
        // `overlaySize`. A max height plus a scrolling body, so a panel that
        // grows a line scrolls inside itself rather than off a 640px phone.
        width: `${PILL_PANEL_WIDTH_PX}px`,
        maxHeight: `${PILL_PANEL_MAX_HEIGHT_PX}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      {/* Why: drag-handle header for repositioning the floating overlay.
          Pointer-only drag is acceptable; the Dismiss button beside the title
          is the keyboard action. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="flex shrink-0 items-center justify-between px-4 pt-4 pb-2"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-2xl">
            {emoji}
          </span>
          <h3 className="font-bold text-(--ee-tw-gray-800)">{title}</h3>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            collapse();
          }}
          // A 32px target rather than the glyph's own 12: this is the control
          // that closes the panel, and a thumb misses a hyphen.
          className="-mr-1 flex h-8 w-8 items-center justify-center text-xl leading-none text-(--ee-tw-gray-400) hover:text-(--ee-tw-gray-600)"
          aria-label="Dismiss options"
        >
          −
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">{body}</div>

      {/* The verbs never scroll away: a panel whose buttons are below its own
          fold is a panel with no actions. */}
      <div className="flex shrink-0 flex-col gap-2 px-4 pt-3 pb-4">{verbs}</div>
    </div>
  );
}
